import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Sparkles, MapPin, Theater, Mountain } from 'lucide-react';

const faqs = [
  {
    q: "Can Rudragarh arrange special setups for birthdays or anniversaries?",
    a: "Yes — contact the team in advance and mention the occasion when booking. Because Rudragarh is a boutique property, special arrangements receive genuine attention rather than a templated response. Whether it's a specific room setup, a table arrangement in the dining hall, or coordinating the evening around your celebration, the team can accommodate personalised requests. The earlier you get in touch, the more we can organise."
  },
  {
    q: "What makes a desert stay a unique birthday gift?",
    a: "A cave room carved into desert rock. Private sand dunes at golden hour. The Thar sky at midnight with no light pollution. Open-air theatre as your evening entertainment. None of these things exist in a city hotel, and none of them can be replicated anywhere outside the Jaisalmer desert. A stay at Rudragarh isn't a gift that gets forgotten — it's an experience that becomes a reference point. 'Remember that cave we stayed in?' is a sentence that gets said for years."
  },
  {
    q: "Is Rudragarh suitable for a milestone anniversary celebration?",
    a: "Yes. The cave stay and the king suite both lend themselves beautifully to milestone occasions — one for its sheer uniqueness, the other for its space and grandeur. The boutique scale of the property means you won't feel like a number; the staff will know the occasion. Private dunes at sunset, a heritage dining hall in the evening, and the open-air theatre under the stars — the elements of a meaningful celebration are all here, without you having to arrange them yourself."
  }
];

const highlights = [
  {
    icon: <Sparkles size={28} />,
    title: "An Experience, Not a Gift Card",
    desc: "A cave in the desert. Private dunes. Stars without light pollution. These are experiences that get mentioned for years — not a voucher filed and forgotten."
  },
  {
    icon: <Mountain size={28} />,
    title: "Private Dunes at Sunset",
    desc: "No tour groups, no strangers. Your own stretch of the Thar at golden hour — the kind of moment that photographs and memories are built from."
  },
  {
    icon: <Theater size={28} />,
    title: "Open-Air Theatre for the Evening",
    desc: "Cultural programs and open-air theatre under the desert sky turn an ordinary evening into a ceremony. The ideal backdrop for a celebration."
  },
  {
    icon: <MapPin size={28} />,
    title: "The Cave Stay — Truly One of a Kind",
    desc: "There is nowhere in India quite like it. Carved rock walls, lantern light, desert silence — a room that defines the word 'unforgettable.'"
  }
];

const rooms = [
  {
    slug: "catacomb-suite",
    name: "The Catacomb Suite",
    price: 10000,
    guests: 4,
    category: "unique",
    desc: "The most memorable room in Jaisalmer — carved into rock, lit by lanterns, surrounded by desert silence. The birthday or anniversary gift that cannot be replicated."
  },
  {
    slug: "grand-quad",
    name: "The Grand Quad",
    price: 7000,
    guests: 4,
    category: "modern",
    desc: "Grand, spacious, and designed for occasions. The king suite offers the comfort and scale that milestone celebrations deserve."
  },
  {
    slug: "heritage-huts",
    name: "The Heritage Huts",
    price: 15000,
    guests: 4,
    category: "unique",
    desc: "Two levels of earthy character with space for four. A distinctive choice for groups celebrating together — adventurous, comfortable, memorable."
  }
];

const BirthdayAnniversary = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Birthday & Anniversary in Jaisalmer | Celebrate at Rudragarh Resort</title>
        <meta name="description" content="Mark your birthday or anniversary in the Jaisalmer desert at Rudragarh — unique cave stays, private dunes, cultural evenings, open-air theatre, and memories that last a lifetime." />
        <meta property="og:title" content="Birthday & Anniversary in Jaisalmer | Celebrate at Rudragarh Resort" />
        <meta property="og:description" content="Mark your birthday or anniversary in the Jaisalmer desert at Rudragarh — unique cave stays, private dunes, cultural evenings, open-air theatre, and memories that last a lifetime." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rudragarh.com/for/birthday-anniversary" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-lawn.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Birthday & Anniversary in Jaisalmer | Rudragarh" />
        <meta name="twitter:description" content="Cave stays, private dunes, cultural evenings — celebrate your birthday or anniversary in the Jaisalmer desert." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-lawn.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/for/birthday-anniversary" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[55vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-lawn.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="overline">Jaisalmer, Rajasthan</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              Celebrate in the <span className="text-gold-500">Desert</span>
            </h1>
            <p className="text-foreground/70 max-w-2xl mx-auto text-lg">
              Birthdays and anniversaries deserve more than a dinner reservation. Rudragarh gives you a cave, private dunes, and a sky full of stars — experiences that last.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <span className="overline">Make It Memorable</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              You can't recreate this <span className="text-gold-500">anywhere else.</span>
            </h2>
            <p className="text-foreground/70 leading-relaxed">
              The best birthday or anniversary gifts share one quality: they're genuinely irreplaceable. A cave room carved into the Jaisalmer desert. A sunset on private dunes with no one else around. A cultural show under the open desert sky that belongs to a world most people only read about. These are not experiences you can find on every travel app — they exist in one place.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              Rudragarh is boutique by design, which means special occasions receive real attention. Contact the team when booking, mention the occasion, and the property will work with you on arrangements — whether it's a specific room setup, an evening under the stars, or something that fits your group's idea of celebration.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              A milestone anniversary in the cave stay. A birthday group in the bamboo duplexes with open-air theatre in the evening. A surprise trip to the king suite. Whatever the shape of the occasion, the desert provides a backdrop that makes the celebration feel earned.
            </p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Why Celebrate at Rudragarh</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Experiences That <span className="text-gold-500">Can't Be Replicated</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 p-8 space-y-4"
              >
                <div className="text-gold-500">{h.icon}</div>
                <h3 className="font-heading text-xl">{h.title}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Where You'll Stay</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Rooms Worthy of the <span className="text-gold-500">Occasion</span>
            </h2>
            <p className="text-foreground/60 mt-4 max-w-xl mx-auto">
              From a carved cave to a grand king suite — the rooms at Rudragarh are experiences in themselves, not just places to sleep.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {rooms.map((room, i) => (
              <motion.div
                key={room.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 group"
              >
                <Link to={`/stay/${room.slug}`}>
                  <div className="p-6 space-y-3">
                    <span className="text-gold-500 text-xs uppercase tracking-widest">{room.category}</span>
                    <h3 className="font-heading text-xl group-hover:text-gold-500 transition-colors">{room.name}</h3>
                    <p className="text-foreground/60 text-sm leading-relaxed">{room.desc}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gold-500/10">
                      <span className="text-gold-500 font-heading text-lg">₹{room.price.toLocaleString()}<span className="text-foreground/40 text-sm font-normal">/night</span></span>
                      <span className="text-foreground/40 text-sm">Up to {room.guests} guests</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/stay" className="btn-outline">View All Rooms</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Common Questions</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Celebrating at <span className="text-gold-500">Rudragarh</span>
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-void-subtle border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={20}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-6 pb-6"
                  >
                    <p className="text-foreground/70 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-lawn.jpg')` }}
        />
        <div className="absolute inset-0 bg-void/80" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <span className="overline">Mark the Occasion</span>
          <h2 className="text-3xl md:text-5xl font-heading font-medium mb-6">
            Give the Gift of the Desert
          </h2>
          <p className="text-foreground/70 mb-10 max-w-xl mx-auto">
            Book your stay at Rudragarh and mention the occasion — we'll make sure the celebration matches the setting.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/book" className="bg-gold-500 text-black px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-400 transition-colors inline-block">
              Book Your Celebration Stay
            </Link>
            <Link to="/contact" className="btn-outline">
              Contact Us About Arrangements
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BirthdayAnniversary;
